import React, { useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  useMediaQuery,
  Stack,
  Skeleton,
  alpha,
} from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { getUserInvoiceData } from "API/calls";
import Panel from "components/elements/Panel";
import { Credit } from "components/icons/components/Credit";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useAPICreditsPaymentTypes } from "hooks/useAPI";
import useConfirm from "hooks/useConfirm";
import { exists } from "i18next";
import { useSnackbar } from "notistack";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CreditsPaymentType } from "types/enums";
import { IUserInvoiceData } from "types/types";
import { getColorByMode } from "utils";
import { renderCredits, renderPrice } from "utils/formatter";

import SelectPaymentMethodModal from "./SelectPaymentMethodModal";

export interface CreditsBundlesTableContent {
  id: CreditsPaymentType;
  amount: number;
  priceCZK: number;
  priceCZKWithVAT: number;
  discount?: number;
}

const tiersDiscount = {
  [CreditsPaymentType.CREDITS_300_V2]: undefined,
  [CreditsPaymentType.CREDITS_600_V2]: 10,
  [CreditsPaymentType.CREDITS_900]: 10,
  [CreditsPaymentType.CREDITS_1800_V2]: 15,
  [CreditsPaymentType.CREDITS_3600_V2]: 20,
  [CreditsPaymentType.CREDITS_5400]: 25,
  [CreditsPaymentType.CREDITS_THIRD_3600]: 33,
  [CreditsPaymentType.CREDITS_THIRD_5400]: 33,
  [CreditsPaymentType.CREDITS_HALF_10000]: 50,
};

interface Props {
  buyEnabled?: boolean;
  actionTiers?: CreditsBundlesTableContent[];
}

const CreditsBundles: React.FC<Props> = ({ buyEnabled, actionTiers, children }) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { enqueueSnackbar } = useSnackbar();
  const [loadingPayment, setLoadingPayment] = useState<CreditsPaymentType | null>(null);
  const [selectPaymentType, setSelectPaymentType] = useState<CreditsPaymentType>();
  const { data } = useAPICreditsPaymentTypes(!actionTiers);
  const { colors } = useMUIThemeModeContext();

  const currentTiers: CreditsBundlesTableContent[] = Object.entries(data?.creditsPaymentTypes ?? {})
    .filter(([key, value]) => value.active === true && !value.recurring)
    .map(([key, value]) => {
      return {
        id: key as CreditsPaymentType,
        priceCZK: value.czkAmountWithoutTax,
        priceCZKWithVAT: value.czkAmountWithTax,
        amount: value.creditsAmount,
        discount: key in tiersDiscount ? tiersDiscount[key as keyof typeof tiersDiscount] : undefined,
      };
    })
    .sort((a, b) => (a ? a.amount : 0) - (b ? b.amount : 0));

  const tiers = actionTiers ? actionTiers : currentTiers;

  const onClick = async (creditsPaymentType: CreditsPaymentType) => {
    setLoadingPayment(creditsPaymentType);
    try {
      const { invoiceData } = (await getUserInvoiceData()).data as IUserInvoiceData;
      if (invoiceData !== null) {
        setSelectPaymentType(creditsPaymentType);
      } else {
        try {
          await confirm({
            allowClose: false,
            title: t("creditsBuy.confirm.title"),
            description: <Trans i18nKey="creditsBuy.confirm.description" />,
            confirmationText: t("creditsBuy.confirm.button"),
          });
          navigate("/nastaveni");
        } catch (err: any) {
          // silent
        } finally {
          setLoadingPayment(null);
        }
      }
    } catch (err: any) {
      enqueueSnackbar(t("creditsBuyError"), { variant: "error" });
    } finally {
      setLoadingPayment(null);
    }
  };

  function renderButton(creditsPaymentType: CreditsPaymentType, credits: number) {
    return (
      <>
        <LoadingButton
          loading={String(loadingPayment) === String(creditsPaymentType)}
          disabled={loadingPayment !== null}
          variant="contained"
          color="success"
          disableElevation
          onClick={() => onClick(creditsPaymentType)}>
          {t("buy")} {renderCredits(credits)}
        </LoadingButton>
      </>
    );
  }

  return (
    <>
      {isMobile && (
        <>
          {children && <Panel sx={{ mb: 4, p: 3, pb: 2 }}>{children}</Panel>}
          {tiers.map((tier, index) => (
            <Panel sx={{ mb: 4, p: 1 }} key={index}>
              <TableContainer>
                <Table>
                  <TableBody
                    sx={{
                      "tr td": { border: 0 },
                      "& > tr td:first-of-type": {
                        fontWeight: fontWeight.medium,
                        fontSize: "0.9375rem",
                        color: getColorByMode(theme, colors.deepMidnightBlue, theme.palette.grey[500]),
                      },
                    }}>
                    <TableRow style={{ backgroundColor: alpha(colors.gray450, 0.1) }}>
                      <TableCell
                        style={{
                          color: theme.palette.text.primary,
                          fontWeight: fontWeight.semiBold,
                          fontSize: "0.875rem",
                        }}>
                        {t("amount")}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            fontWeight: fontWeight.medium,
                            fontSize: "1.125rem",
                            display: "inline-flex",
                            justifyContent: "end",
                            alignItems: "center",
                          }}>
                          {new Intl.NumberFormat("cs-CZ", {}).format(tier.amount)}
                          <Credit color="primary" sx={{ ml: 0.5, fontSize: 20 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow style={{ backgroundColor: alpha(colors.gray450, 0.05) }}>
                      <TableCell>{t("creditsPackageSubscriptionLabel.title")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: fontWeight.light }}>
                        {exists("creditsPackageSubscriptionLabel." + tier.id) ? (
                          <Trans
                            i18nKey={"creditsPackageSubscriptionLabel." + tier.id}
                            components={{
                              strong: (
                                <Typography component="span" fontWeight={fontWeight.medium} fontSize={"inherit"} />
                              ),
                            }}
                          />
                        ) : (
                          "---"
                        )}
                      </TableCell>
                    </TableRow>
                    {tier.discount && (
                      <TableRow style={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableCell style={{ color: theme.palette.primary.main }}>{t("discount")}</TableCell>
                        <TableCell align="right" sx={{ borderRadius: "0 10px 10px 0" }}>
                          <Typography
                            color="primary.main"
                            fontSize="1rem"
                            fontWeight={fontWeight.semiBold}
                            component="div"
                            sx={{ display: "flex", alignItems: "center", justifyContent: "end" }}>
                            {t("youSave")} {tier.discount}&nbsp;%
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>{t("price")}</TableCell>
                      <TableCell align="right">
                        <Stack direction="column">
                          <Typography
                            sx={{ color: getColorByMode(theme, colors.deepMidnightBlue, theme.palette.grey[500]) }}
                            fontWeight={fontWeight.semiBold}
                            fontSize={"0.875rem"}>
                            {renderPrice(tier.priceCZKWithVAT)}
                          </Typography>
                          {tier.priceCZK && tier.priceCZK !== tier.priceCZKWithVAT && (
                            <Typography
                              sx={{ color: colors.gray400 }}
                              fontWeight={fontWeight.regular}
                              fontSize={"0.75rem"}>
                              {t("withoutTax", { price: renderPrice(tier.priceCZK) })}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {buyEnabled && (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          {renderButton(tier.id, tier.amount)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Panel>
          ))}
        </>
      )}

      {!isMobile && (
        <>
          {children}
          <TableContainer>
            <Table>
              <TableBody
                sx={{
                  "tr td": { border: 0 },
                  "& > tr td:first-of-type": {
                    fontWeight: fontWeight.medium,
                    fontSize: "0.9375rem",
                    color: getColorByMode(theme, colors.deepMidnightBlue, theme.palette.grey[500]),
                  },
                }}>
                <TableRow style={{ backgroundColor: alpha(colors.gray450, 0.1) }}>
                  <TableCell
                    align="left"
                    sx={{ width: "20%" }}
                    style={{ color: theme.palette.text.primary, fontWeight: fontWeight.semiBold, fontSize: "1rem" }}>
                    {t("amount")}
                  </TableCell>
                  {tiers.map((tier, index) => (
                    <TableCell align="center" sx={{ width: "20%" }} key={index}>
                      <Typography
                        fontSize="1.25rem"
                        fontWeight={fontWeight.medium}
                        component="div"
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Box>{new Intl.NumberFormat("cs-CZ", {}).format(tier.amount)}</Box>
                        <Credit color="primary" sx={{ ml: 1, fontSize: 24 }} />
                      </Typography>
                    </TableCell>
                  ))}
                  {tiers.length === 0 && (
                    <TableCell align="center" colSpan={4}>
                      <Skeleton variant="text" />
                    </TableCell>
                  )}
                </TableRow>
                <TableRow style={{ backgroundColor: alpha(colors.gray450, 0.05) }}>
                  <TableCell align="left" sx={{ width: "20%" }}>
                    {t("creditsPackageSubscriptionLabel.title")}
                  </TableCell>
                  {tiers.map((tier, index) => (
                    <TableCell align="center" key={index} sx={{ fontWeight: fontWeight.light }}>
                      {exists("creditsPackageSubscriptionLabel." + tier.id) ? (
                        <Trans
                          i18nKey={"creditsPackageSubscriptionLabel." + tier.id}
                          components={{
                            strong: <Typography component="span" fontWeight={fontWeight.medium} fontSize={"inherit"} />,
                          }}
                        />
                      ) : (
                        "---"
                      )}
                    </TableCell>
                  ))}
                  {tiers.length === 0 && (
                    <TableCell align="center" colSpan={4}>
                      <Skeleton variant="text" />
                    </TableCell>
                  )}
                </TableRow>
                <TableRow style={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell align="left" sx={{ width: "20%" }} style={{ color: theme.palette.primary.main }}>
                    {t("discount")}
                  </TableCell>
                  {tiers.map((tier, index) => (
                    <TableCell align="center" sx={{ width: "20%" }} key={index}>
                      <Typography
                        color="primary.main"
                        fontSize="1rem"
                        fontWeight={fontWeight.semiBold}
                        component="div"
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {tier.discount && (
                          <>
                            {t("youSave")} {tier.discount}&nbsp;%
                          </>
                        )}
                        {!tier.discount && <>---</>}
                      </Typography>
                    </TableCell>
                  ))}
                  {tiers.length === 0 && (
                    <TableCell align="center" colSpan={4}>
                      <Skeleton variant="text" />
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell>{t("price")}</TableCell>
                  {tiers.map((tier, index) => (
                    <TableCell align="center" key={index}>
                      <Stack direction="column" alignItems="center">
                        <Typography
                          sx={{ color: getColorByMode(theme, colors.deepMidnightBlue, theme.palette.grey[500]) }}
                          fontWeight={fontWeight.semiBold}
                          fontSize={"1rem"}>
                          {renderPrice(tier.priceCZKWithVAT)}
                        </Typography>
                        {tier.priceCZK && tier.priceCZK !== tier.priceCZKWithVAT && (
                          <Typography
                            sx={{ color: colors.gray400 }}
                            fontWeight={fontWeight.regular}
                            fontSize={"0.8125rem"}>
                            {t("withoutTax", { price: renderPrice(tier.priceCZK) })}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  ))}
                  {tiers.length === 0 && (
                    <TableCell align="center" colSpan={4}>
                      <Skeleton variant="text" />
                    </TableCell>
                  )}
                </TableRow>
                {buyEnabled && (
                  <TableRow>
                    <TableCell />
                    {tiers.map((tier, index) => (
                      <TableCell align="center" key={index}>
                        {renderButton(tier.id, tier.amount)}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {selectPaymentType && (
        <SelectPaymentMethodModal
          open={Boolean(selectPaymentType)}
          handleClose={() => setSelectPaymentType(undefined)}
          creditsPaymentType={selectPaymentType}
        />
      )}
    </>
  );
};

export default CreditsBundles;
